import React, { useState, useMemo } from 'react';
import { useAuth } from './AuthContext';
import {
    Container,
    Box,
    Grid,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Collapse,
    Tooltip
} from '@mui/material';
import {
    PercentOutlined as PercentIcon,
    ShowChart as ChartIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Star,
    StarBorder
} from '@mui/icons-material';
import { useFavoriteMetrics } from '../hooks/useFavoriteMetrics';
import { useTheme } from '@mui/material/styles';
import TimeRangeButtons from './TimeRangeButtons';
import { formatLargeNumber } from './StockChart/utils/formatters';
import FinancialChartModal from './FinancialChartModal';

const CompanyCashStatement = ({ data: cashStatementData, growthData: cashStatementGrowthData }) => {
    const { user } = useAuth();
    const theme = useTheme();
    const [timeRange, setTimeRange] = useState('5Y');
    const [periodType, setPeriodType] = useState('annual');
    const [expandedRows, setExpandedRows] = useState({});
    const [chartModal, setChartModal] = useState({ open: false, metric: null });

    const processedData = useMemo(() => {
        if (!cashStatementData?.annual || !cashStatementData?.quarterly) return null;
        return {
            annual: cashStatementData.annual,
            quarterly: cashStatementData.quarterly
        };
    }, [cashStatementData]);

    const processedGrowthData = useMemo(() => {
        if (!cashStatementGrowthData?.annual || !cashStatementGrowthData?.quarterly) return null;
        return {
            annual: cashStatementGrowthData.annual,
            quarterly: cashStatementGrowthData.quarterly
        };
    }, [cashStatementGrowthData]);

    const currentData = useMemo(() => {
        if (!processedData) return [];
        if (periodType === 'annual') {
            return processedData.annual;
        } else {
            return Object.values(processedData.quarterly)
                .flat()
                .sort((a, b) => new Date(a.date) - new Date(b.date));
        }
    }, [processedData, periodType]);

    const currentGrowthData = useMemo(() => {
        if (!processedGrowthData) return [];
        if (periodType === 'annual') {
            return processedGrowthData.annual;
        } else {
            return Object.values(processedGrowthData.quarterly)
                .flat()
                .sort((a, b) => new Date(a.date) - new Date(b.date));
        }
    }, [processedGrowthData, periodType]);

    const getFilteredData = (data) => {
        if (!data || !data.length) return [];
        const sortedData = [...data].sort((a, b) => new Date(b.date) - new Date(a.date));
        if (timeRange === 'MAX') {
            return sortedData;
        }
        const yearLimit = {
            '1Y': 1,
            '3Y': 3,
            '5Y': 5,
            '10Y': 10
        }[timeRange];
        return sortedData.slice(0, yearLimit);
    };

    const filteredData = getFilteredData(currentData);

    const filteredGrowthData = useMemo(() => {
        if (!currentGrowthData.length || !filteredData.length) return [];
        const growthMap = new Map();
        currentGrowthData.forEach(growth => {
            const year = growth.fiscalYear;
            const period = growth.period || 'FY';
            const key = `${period}-${year}`;
            growthMap.set(key, growth);
        });
        return filteredData.map(statement => {
            const year = statement.fiscalYear;
            const period = statement.period || 'FY';
            const key = `${period}-${year}`;
            return growthMap.get(key) || null;
        });
    }, [currentGrowthData, filteredData]);

    const toggleRowExpansion = (rowKey) => {
        setExpandedRows(prev => ({
            ...prev,
            [rowKey]: !prev[rowKey]
        }));
    };

    // Define which metrics get which chart type
    const getChartType = (metricKey) => {
        const tier1 = ['netCashProvidedByOperatingActivities', 'freeCashFlow', 'netChangeInCash', 'operatingCashFlow', 'capitalExpenditure'];
        const tier2 = ['netIncome', 'depreciationAndAmortization', 'investmentsInPropertyPlantAndEquipment', 'netCashProvidedByInvestingActivities', 'netCashProvidedByFinancingActivities'];

        if (tier1.includes(metricKey)) return 'combo';
        if (tier2.includes(metricKey)) return 'line';
        return null;
    };

    const handleChartClick = (metric) => {
        setChartModal({ open: true, metric });
    };

    const handleCloseChart = () => {
        setChartModal({ open: false, metric: null });
    };

    const formatValue = (value) => {
        if (value === null || value === undefined) return '-';
        if (typeof value === 'number') {
            return formatLargeNumber(value);
        }
        return value;
    };

    const formatGrowthValue = (value) => {
        if (value === null || value === undefined) return '-';
        const percentage = (value * 100);
        const formatted = percentage.toFixed(2);
        return `${formatted}%`;
    };

    const getGrowthColor = (value) => {
        if (value === null || value === undefined) return theme.palette.text.secondary;
        return value >= 0 ? theme.palette.positive.main : theme.palette.negative.main;
    };

    // Cash Flow Statement metrics - ordered by importance
    const metrics = [
        // Tier 1: Key Cash Flow Metrics - Most Important
        { key: 'netCashProvidedByOperatingActivities', label: 'Operating Cash Flow', growthKey: 'growthNetCashProvidedByOperatingActivities' },
        { key: 'freeCashFlow', label: 'Free Cash Flow', growthKey: 'growthFreeCashFlow' },
        { key: 'netChangeInCash', label: 'Net Change in Cash', growthKey: 'growthNetChangeInCash' },
        { key: 'cashAtEndOfPeriod', label: 'Cash at End of Period', growthKey: 'growthCashAtEndOfPeriod' },
        { key: 'cashAtBeginningOfPeriod', label: 'Cash at Beginning of Period', growthKey: 'growthCashAtBeginningOfPeriod' },

        // Tier 2: Operating Activities Detail
        { key: 'netIncome', label: 'Net Income', growthKey: 'growthNetIncome' },
        { key: 'depreciationAndAmortization', label: 'Depreciation & Amortization', growthKey: 'growthDepreciationAndAmortization' },
        { key: 'deferredIncomeTax', label: 'Deferred Income Tax', growthKey: 'growthDeferredIncomeTax' },
        { key: 'stockBasedCompensation', label: 'Stock Based Compensation', growthKey: 'growthStockBasedCompensation' },
        { key: 'changeInWorkingCapital', label: 'Change in Working Capital', growthKey: 'growthChangeInWorkingCapital' },
        { key: 'accountsReceivables', label: 'Accounts Receivables', growthKey: 'growthAccountsReceivables' },
        { key: 'inventory', label: 'Inventory', growthKey: 'growthInventory' },
        { key: 'accountsPayables', label: 'Accounts Payables', growthKey: 'growthAccountsPayables' },
        { key: 'otherWorkingCapital', label: 'Other Working Capital', growthKey: 'growthOtherWorkingCapital' },
        { key: 'otherNonCashItems', label: 'Other Non-Cash Items', growthKey: 'growthOtherNonCashItems' },

        // Tier 3: Investing Activities
        { key: 'netCashProvidedByInvestingActivities', label: 'Net Cash from Investing Activities', growthKey: 'growthNetCashUsedForInvestingActivites' },
        { key: 'investmentsInPropertyPlantAndEquipment', label: 'Investments in Property, Plant & Equipment', growthKey: 'growthInvestmentsInPropertyPlantAndEquipment' },
        { key: 'capitalExpenditure', label: 'Capital Expenditure', growthKey: 'growthCapitalExpenditure' },
        { key: 'acquisitionsNet', label: 'Acquisitions (Net)', growthKey: 'growthAcquisitionsNet' },
        { key: 'purchasesOfInvestments', label: 'Purchases of Investments', growthKey: 'growthPurchasesOfInvestments' },
        { key: 'salesMaturitiesOfInvestments', label: 'Sales/Maturities of Investments', growthKey: 'growthSalesMaturitiesOfInvestments' },
        { key: 'otherInvestingActivities', label: 'Other Investing Activities', growthKey: 'growthOtherInvestingActivites' },

        // Tier 4: Financing Activities
        { key: 'netCashProvidedByFinancingActivities', label: 'Net Cash from Financing Activities', growthKey: 'growthNetCashUsedProvidedByFinancingActivities' },
        { key: 'netDebtIssuance', label: 'Net Debt Issuance', growthKey: 'growthDebtRepayment' },
        { key: 'longTermNetDebtIssuance', label: 'Long-Term Net Debt Issuance', growthKey: 'growthLongTermDebtIssuance' },
        { key: 'shortTermNetDebtIssuance', label: 'Short-Term Net Debt Issuance', growthKey: 'growthShortTermDebtPayment' },
        { key: 'commonStockIssuance', label: 'Common Stock Issuance', growthKey: 'growthCommonStockIssued' },
        { key: 'commonStockRepurchased', label: 'Common Stock Repurchased', growthKey: 'growthCommonStockRepurchased' },
        { key: 'netCommonStockIssuance', label: 'Net Common Stock Issuance', growthKey: 'growthCommonStockIssuance' },
        { key: 'netDividendsPaid', label: 'Dividends Paid', growthKey: 'growthDividendsPaid' },
        { key: 'commonDividendsPaid', label: 'Common Dividends Paid', growthKey: 'growthDividendsPaidCommonStock' },
        { key: 'preferredDividendsPaid', label: 'Preferred Dividends Paid', growthKey: 'growthDividendsPaidPreferredStock' },
        { key: 'otherFinancingActivities', label: 'Other Financing Activities', growthKey: 'growthOtherFinancingActivites' },

        // Tier 5: Additional Metrics
        { key: 'effectOfForexChangesOnCash', label: 'Effect of Forex Changes on Cash', growthKey: 'growthEffectOfForexChangesOnCash' },
        { key: 'operatingCashFlow', label: 'Operating Cash Flow', growthKey: 'growthOperatingCashFlow' },
        { key: 'incomeTaxesPaid', label: 'Income Taxes Paid', growthKey: 'growthIncomeTaxesPaid' },
        { key: 'interestPaid', label: 'Interest Paid', growthKey: 'growthInterestPaid' }
    ];

    // Use favorite metrics hook
    const { sortedMetrics, toggleFavorite, isFavorite } = useFavoriteMetrics('cash', metrics);

    if (!processedData) {
        return (
            <Container>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <Typography variant="h6" color="textSecondary">
                        No cash flow statement data available
                    </Typography>
                </Box>
            </Container>
        );
    }

    return (
        <Container
            maxWidth="xl"
            sx={{ px: { xs: 0, sm: 2, md: 3 } }}
        >
            <Box my={4}>
                <Grid
                    container
                    spacing={2}
                    alignItems="center"
                    justifyContent="center"
                    sx={{ mb: 3, px: { xs: 2, sm: 0 } }}
                >
                    <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box sx={{ mb: 2, width: '100%' }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Period Type</InputLabel>
                                <Select
                                    value={periodType}
                                    label="Period Type"
                                    onChange={(e) => setPeriodType(e.target.value)}
                                >
                                    <MenuItem value="annual">Annual</MenuItem>
                                    <MenuItem value="quarter">Quarterly</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                        <TimeRangeButtons
                            periodType={periodType}
                            timeRange={timeRange}
                            setTimeRange={setTimeRange}
                            userPlan={user.plan.name}
                            size="medium"
                        />
                    </Grid>
                </Grid>

                <TableContainer
                    component={Paper}
                    variant="stats"
                    elevation={3}
                    sx={{
                        overflowX: 'auto',
                        WebkitOverflowScrolling: 'touch',
                        '& .MuiTable-root': {
                            minWidth: { xs: '800px', sm: 'auto' }
                        }
                    }}
                >
                    <Table sx={{ tableLayout: 'fixed' }}>
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sx={{
                                        fontWeight: 'bold',
                                        width: { xs: '180px', sm: '250px' },
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                        py: { xs: 0.75, sm: 1.5 },
                                        px: { xs: 1, sm: 2 }
                                    }}
                                >
                                    Metric
                                </TableCell>
                                {filteredData.map((statement, index) => (
                                    <TableCell
                                        key={index}
                                        align="right"
                                        sx={{
                                            fontWeight: 'bold',
                                            width: { xs: '100px', sm: '150px' },
                                            fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                            py: { xs: 0.75, sm: 1.5 },
                                            px: { xs: 1, sm: 2 }
                                        }}
                                    >
                                        {periodType === 'annual'
                                            ? `FY ${statement.fiscalYear || new Date(statement.date).getFullYear()}`
                                            : `${statement.period} ${statement.fiscalYear || new Date(statement.date).getFullYear()}`
                                        }
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedMetrics.map((metric) => {
                                const chartType = getChartType(metric.key);
                                return (
                                    <React.Fragment key={metric.key}>
                                        <TableRow
                                            hover
                                            sx={{
                                                '&:hover': {
                                                    backgroundColor: theme.palette.mode === 'dark'
                                                        ? 'rgba(255, 255, 255, 0.05)'
                                                        : 'rgba(0, 0, 0, 0.02)'
                                                }
                                            }}
                                        >
                                            <TableCell
                                                sx={{
                                                    width: { xs: '180px', sm: '250px' },
                                                    py: { xs: 0.75, sm: 1.5 },
                                                    px: { xs: 1, sm: 2 }
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                                                    {/* Star Icon for Favorites */}
                                                    <Tooltip title={isFavorite(metric.key) ? "Remove from favorites" : "Add to favorites"}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => toggleFavorite(metric.key)}
                                                            sx={{
                                                                color: isFavorite(metric.key)
                                                                    ? theme.palette.warning.main
                                                                    : theme.palette.text.disabled,
                                                                p: { xs: 0.25, sm: 0.5 }
                                                            }}
                                                        >
                                                            {isFavorite(metric.key) ?
                                                                <Star fontSize="small" /> :
                                                                <StarBorder fontSize="small" />
                                                            }
                                                        </IconButton>
                                                    </Tooltip>

                                                    <Tooltip title="Toggle Growth">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => toggleRowExpansion(metric.key)}
                                                            sx={{
                                                                color: expandedRows[metric.key]
                                                                    ? theme.palette.primary.main
                                                                    : theme.palette.text.secondary,
                                                                p: { xs: 0.25, sm: 0.5 }
                                                            }}
                                                        >
                                                            {expandedRows[metric.key] ?
                                                                <ExpandLessIcon fontSize="small" /> :
                                                                <ExpandMoreIcon fontSize="small" />
                                                            }
                                                        </IconButton>
                                                    </Tooltip>

                                                    {chartType && (
                                                        <Tooltip title="View Chart">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleChartClick(metric)}
                                                                sx={{
                                                                    color: theme.palette.text.secondary,
                                                                    p: { xs: 0.25, sm: 0.5 }
                                                                }}
                                                            >
                                                                <ChartIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: 500,
                                                            fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                                        }}
                                                    >
                                                        {metric.label}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            {filteredData.map((statement, index) => (
                                                <TableCell
                                                    key={index}
                                                    align="right"
                                                    sx={{
                                                        width: { xs: '100px', sm: '150px' },
                                                        py: { xs: 0.75, sm: 1.5 },
                                                        px: { xs: 1, sm: 2 }
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
                                                    >
                                                        {formatValue(statement[metric.key])}
                                                    </Typography>
                                                </TableCell>
                                            ))}
                                        </TableRow>

                                        <TableRow>
                                            <TableCell
                                                colSpan={filteredData.length + 1}
                                                sx={{ p: 0, border: 0 }}
                                            >
                                                <Collapse in={expandedRows[metric.key]} timeout="auto" unmountOnExit>
                                                    <Box
                                                        sx={{
                                                            backgroundColor: theme.palette.mode === 'dark'
                                                                ? 'rgba(255, 255, 255, 0.03)'
                                                                : 'rgba(0, 0, 0, 0.02)',
                                                            borderLeft: `3px solid ${theme.palette.primary.main}`
                                                        }}
                                                    >
                                                        <Table sx={{ tableLayout: 'fixed' }}>
                                                            <TableBody>
                                                                <TableRow>
                                                                    <TableCell
                                                                        sx={{
                                                                            width: { xs: '180px', sm: '250px' },
                                                                            pl: { xs: 4, sm: 8 },
                                                                            py: { xs: 0.75, sm: 1.5 },
                                                                            px: { xs: 1, sm: 2 },
                                                                            border: 0
                                                                        }}
                                                                    >
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                                                                            <PercentIcon
                                                                                fontSize="small"
                                                                                sx={{
                                                                                    color: theme.palette.text.secondary,
                                                                                    fontSize: { xs: '0.9rem', sm: '1.25rem' }
                                                                                }}
                                                                            />
                                                                            <Typography
                                                                                variant="body2"
                                                                                sx={{
                                                                                    fontStyle: 'italic',
                                                                                    color: theme.palette.text.secondary,
                                                                                    fontSize: { xs: '0.7rem', sm: '0.875rem' }
                                                                                }}
                                                                            >
                                                                                {metric.label} Growth (YoY)
                                                                            </Typography>
                                                                        </Box>
                                                                    </TableCell>
                                                                    {filteredGrowthData.map((growthStatement, index) => (
                                                                        <TableCell
                                                                            key={index}
                                                                            align="right"
                                                                            sx={{
                                                                                width: { xs: '100px', sm: '150px' },
                                                                                py: { xs: 0.75, sm: 1.5 },
                                                                                px: { xs: 1, sm: 2 },
                                                                                border: 0
                                                                            }}
                                                                        >
                                                                            <Typography
                                                                                variant="body2"
                                                                                sx={{
                                                                                    color: growthStatement
                                                                                        ? getGrowthColor(growthStatement[metric.growthKey])
                                                                                        : theme.palette.text.secondary,
                                                                                    fontWeight: 600,
                                                                                    fontSize: { xs: '0.7rem', sm: '0.875rem' }
                                                                                }}
                                                                            >
                                                                                {growthStatement
                                                                                    ? formatGrowthValue(growthStatement[metric.growthKey])
                                                                                    : '-'
                                                                                }
                                                                            </Typography>
                                                                        </TableCell>
                                                                    ))}
                                                                </TableRow>
                                                            </TableBody>
                                                        </Table>
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Chart Modal */}
            {chartModal.open && chartModal.metric && (
                <FinancialChartModal
                    open={chartModal.open}
                    onClose={handleCloseChart}
                    metricLabel={chartModal.metric.label}
                    chartType={getChartType(chartModal.metric.key)}
                    filteredData={filteredData}
                    filteredGrowthData={filteredGrowthData}
                    metricKey={chartModal.metric.key}
                    growthKey={chartModal.metric.growthKey}
                    periodType={periodType}
                />
            )}
        </Container>
    );
};

export default CompanyCashStatement;